import { put, call, getContext, select, cancel, take } from 'redux-saga/effects';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { publishOffer as publishOfferRequest } from '../../../../services/publishOffer';
import { IAppState } from '../../../types';
import {
  EPublishOfferErrorCode,
  OFFER_VALIDATION_FAIL,
  OFFER_VALIDATION_SUCCESS,
  publishOfferFail,
  publishOfferStart,
  publishOfferSuccess,
  PUBLISH_OFFER_FAIL,
  PUBLISH_OFFER_START,
  PUBLISH_OFFER_SUCCESS,
  TPublishOfferAction,
  updateOffer,
  setIsSubagentNotEnoughMoney,
} from '../../../reducers';
import { clone } from 'ramda';
import { getPublishParams, mapPublishTerms, checkSubagentNotEnoughMoneyError, getPublishTerms } from './utils';
import { selectIsArchived, selectIsFromXml } from '../../../../selectors/offer';
import {
  selectActivePackage,
  selectActivePlacingType,
  selectActiveService,
  selectIsMultiPostingAvailable,
  selectPublicationPeriod,
  selectServiceUnavailableReason,
  selectIsWithoutServices,
  selectIsIncreaseToMinimalBet,
  selectPlacingMoneySource,
  selectPublicationTokens,
} from '../../../../selectors/publishTerms';
import { selectTermsDuration } from '../../../../selectors/termsDurations';
import { selectIsAuctionSelected, selectMinimalBet } from '../../../../selectors/auction';
import { selectIsAutoProlongAvailable, selectIsAutoProlongChecked } from '../../../../selectors/checkoutInfo';
import { scrollToElement, sendEvent } from '../../../../utils';
import { checkPublish } from '../checkPublish';
import { selectIsPublishToHiddenBase } from '../../../../selectors/publishAvailable';
import { TValidationAction } from './types';
import { selectConfigValue } from '../../../../selectors/config';
import { TCheckPublishResult } from '../../../../services/checkPublish';
import { PROMOTION_SECTION_ID } from '../../../../constants/nodeIds';
import { PUBLISH_TERMS_SERVICE_UNAVAILABLE_REASON } from '../../../../constants/events';

export function* publishOffer(action: TPublishOfferAction) {
  const { isPremoderationSkip = false } = action.payload;

  const state: IAppState = yield select();

  const unavailableReason = selectServiceUnavailableReason(state);
  const isPublishToHiddenBase = selectIsPublishToHiddenBase(state);

  if (unavailableReason && !isPublishToHiddenBase) {
    sendEvent(PUBLISH_TERMS_SERVICE_UNAVAILABLE_REASON);
    scrollToElement(PROMOTION_SECTION_ID);

    yield cancel();
  }

  yield put(publishOfferStart());

  sendEvent(PUBLISH_OFFER_START, { isPremoderationSkip });

  const validationAction: TValidationAction = yield take([OFFER_VALIDATION_SUCCESS, OFFER_VALIDATION_FAIL]);

  if (validationAction.type === OFFER_VALIDATION_FAIL) {
    const validationError = {
      errorCode: EPublishOfferErrorCode.validationError,
      errorMessage: null,
      errors: null,
    };

    yield put(publishOfferFail(validationError));

    yield cancel();

    return;
  }

  const httpApi: IHttpApi = yield getContext('httpApi');
  const logger: ILogger = yield getContext('logger');
  const newState: IAppState = yield select();

  const service = selectActiveService(newState);
  const servicePackage = selectActivePackage(newState);
  const publicationPeriod = selectPublicationPeriod(newState);
  const isAuctionSelected = selectIsAuctionSelected(newState);
  const placingType = selectActivePlacingType(newState);
  const isAutoprolongAvailable = selectIsAutoProlongAvailable(newState);
  const isAutoprolongChecked = selectIsAutoProlongChecked(newState);
  const isArchived = selectIsArchived(newState);
  const isFromXml = selectIsFromXml(newState);
  const isMultiPostingAvailable = selectIsMultiPostingAvailable(newState);
  const subdomain = selectConfigValue('subdomain', newState);
  const increaseToMinimalBet = selectIsIncreaseToMinimalBet(state);
  const isWithoutServices = selectIsWithoutServices(state);
  const minimalBet = selectMinimalBet(state);
  const moneySource = selectPlacingMoneySource(state);
  const termsDuration = selectTermsDuration(state);
  const publicationTokens = selectPublicationTokens(state);

  const { auction, calltracking } = newState;

  const offer = clone(newState.offer);

  if (!isMultiPostingAvailable) {
    offer.multiposting = {
      enabled: false,
    };
  }

  const terms = getPublishTerms({
    service,
    servicePackage,
    publicationPeriod,
    placingType,
    auction,
    isAuctionSelected,
    calltracking,
    increaseToMinimalBet,
    isWithoutServices,
    minimalBet,
    moneySource,
    termsDuration,
  });

  // При публикации в скрытую базу, check-publish дергать не нужно
  if (!isPublishToHiddenBase) {
    const checkPublishResult: TCheckPublishResult = yield call(checkPublish, terms);

    if (!checkPublishResult.success) {
      const { errorCode } = checkPublishResult.payload;

      yield put(
        publishOfferFail({
          errorCode,
          errorMessage: null,
          errors: null,
        }),
      );

      sendEvent(PUBLISH_OFFER_FAIL, { statusCode: 0, payload: checkPublishResult.payload });

      yield cancel();

      return;
    }
  }

  const publishTerms = mapPublishTerms({ isAutoprolongAvailable, isAutoprolongChecked, terms, publicationTokens });

  const parameters = getPublishParams({
    offer,
    calltracking,
    publishTerms,
    isArchived,
    isFromXml,
  });

  const { success, statusCode, payload } = yield call(publishOfferRequest, {
    httpApi,
    logger,
    parameters,
    subdomain,
  });

  if (success) {
    yield put(publishOfferSuccess());
    yield put(updateOffer(payload));

    sendEvent(PUBLISH_OFFER_SUCCESS, payload);
  } else {
    yield put(publishOfferFail(payload));

    const isSubagentNotEnoughMoney = checkSubagentNotEnoughMoneyError(payload.errors);
    if (isSubagentNotEnoughMoney) {
      yield put(setIsSubagentNotEnoughMoney(true));
    }

    sendEvent(PUBLISH_OFFER_FAIL, { statusCode: isSubagentNotEnoughMoney ? 0 : statusCode, payload });
  }
}
